// PinIntelSection — May 7, 2026
//
// Shows Regrid-derived parcel intel for a knock pin in the close-up sheet.
// Fields are user-configurable via the ⚙ button → checklist modal,
// persisted to localStorage. Defaults to all 14 fields shown.
//
// Pulls the parcel row via knock_pins.parcel_id (already stamped by the
// BEFORE INSERT/UPDATE trigger). Renders nothing if no parcel match.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";

const STORAGE_KEY = "bardin.canvas.pinFields.v2";  // v2 (May 7) — default trimmed to 4 high-signal fields

interface ParcelRow {
  id: string;
  owner_name: string | null;
  owner_type: string | null;
  year_built: number | null;
  building_sqft: number | null;
  footprint_sqft: number | null;
  stories: number | null;
  estimated_squares: number | null;
  lot_sqft: number | null;
  last_sale_date: string | null;
  last_sale_price: number | null;
  use_code: string | null;
  qualifies: boolean | null;
  is_preferred: boolean | null;
  census_median_age: number | null;
  raw: any;
}

// Estimate likely homeowner age band from block-group median age + tenure proxy.
// Returns a decade band like "60s" or "unknown". Conservative: if last sale was
// recent (<2 years) we can't infer tenure adjustment so we return unknown.
//
// Method:
//   - Start with block-group median age (Census ACS, denormalized on parcel).
//   - If owned for ≥5 years, add (tenure_years × 0.5), capped at +25.
//   - Round to nearest decade.
function estimateAgeBand(p: ParcelRow): string | null {
  const median = p.census_median_age;
  if (median == null) return null;
  let est = Number(median);

  if (p.last_sale_date) {
    const sold = new Date(p.last_sale_date);
    const years = (Date.now() - sold.getTime()) / (365.25 * 86400 * 1000);
    if (years < 2) return "unknown"; // recent sale → no tenure signal
    if (years >= 5) est += Math.min(25, years * 0.5);
  } else {
    // No sale date — use block group median as-is
  }

  // Round to nearest decade band: 30s / 40s / 50s / 60s / 70s+
  const decade = Math.round(est / 10) * 10;
  if (decade <= 20) return "20s";
  if (decade >= 70) return "70s+";
  return `${decade}s`;
}

// ── Field registry ─────────────────────────────────────────────────────
// Each entry: { key, label, format(parcel) → string|null }
// Toggle keys are stable — don't rename without bumping STORAGE_KEY.
interface FieldDef {
  key: string;
  label: string;
  format: (p: ParcelRow) => string | null;
}

const FIELDS: FieldDef[] = [
  { key: "homeowner",       label: "Homeowner",        format: (p) => titleCaseOwner(p.owner_name) },
  { key: "likely_age",      label: "Likely age",       format: (p) => estimateAgeBand(p) },
  { key: "owner_type",      label: "Owner type",       format: (p) => p.owner_type },
  { key: "year_built",      label: "Year built",       format: (p) => p.year_built ? `${p.year_built} (${new Date().getFullYear() - p.year_built} yrs)` : null },
  { key: "stories",         label: "Stories",          format: (p) => p.stories != null ? String(p.stories) : null },
  { key: "building_sqft",   label: "Building sqft",    format: (p) => fmtInt(p.building_sqft) },
  { key: "footprint_sqft",  label: "Footprint sqft",   format: (p) => fmtInt(p.footprint_sqft) },
  { key: "est_squares",     label: "Est. roof squares ⭐", format: (p) => p.estimated_squares != null ? `~${p.estimated_squares} sq` : null },
  { key: "lot_size",        label: "Lot size",         format: (p) => p.lot_sqft ? `${fmtInt(p.lot_sqft)} sf · ${(p.lot_sqft / 43560).toFixed(2)} ac` : null },
  { key: "last_sale",       label: "Last sale",        format: (p) => p.last_sale_date ? `${p.last_sale_date}${p.last_sale_price ? ` · $${Number(p.last_sale_price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}` : null },
  { key: "use_code",        label: "Use code",         format: (p) => p.use_code },
  { key: "beds_baths",      label: "Beds / Baths",     format: (p) => {
    const b = p.raw?.num_bedrooms;
    const ba = p.raw?.num_bath;
    if (!b && !ba) return null;
    return `${b ?? "?"} bed / ${ba ?? "?"} bath`;
  }},
  { key: "flood_zone",      label: "Flood zone",       format: (p) => p.raw?.fema_flood_zone
    ? `${p.raw.fema_flood_zone}${p.raw.fema_nri_risk ? ` · ${p.raw.fema_nri_risk}` : ""}`
    : null },
  { key: "subdivision",     label: "Subdivision",      format: (p) => p.raw?.subdivision || p.raw?.neighborhood || null },
  { key: "usps_deliv",      label: "USPS deliverability", format: (p) => {
    const dpv = p.raw?.dpv_status;
    const vac = p.raw?.usps_vacancy === "Y";
    if (!dpv && !vac) return null;
    return `${dpv === "V" ? "✓ Valid" : dpv ?? "—"}${vac ? " · ⚠ vacant" : ""}`;
  }},
];

const ALL_KEYS = FIELDS.map((f) => f.key);

// Default fields shown when a user has no saved preference. Picked for
// roofing-canvasser signal: who lives there, when the roof was last new,
// roughly how many squares to bid, when they last bought (good proxy for
// how long they've owned). Everything else is one click away via the ⚙.
const DEFAULT_KEYS = ["homeowner", "likely_age", "year_built", "est_squares", "last_sale"];

function loadEnabled(): Set<string> {
  if (typeof window === "undefined") return new Set(DEFAULT_KEYS);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set(DEFAULT_KEYS);
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.filter((k) => typeof k === "string"));
  } catch { /* ignore */ }
  return new Set(DEFAULT_KEYS);
}

function saveEnabled(set: Set<string>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch { /* ignore */ }
}

export default function PinIntelSection({ parcelId }: { parcelId: string | null }) {
  const [parcel, setParcel] = useState<ParcelRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState<Set<string>>(() => loadEnabled());
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if (!parcelId) { setParcel(null); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("parcels")
        .select("id, owner_name, owner_type, year_built, building_sqft, footprint_sqft, stories, estimated_squares, lot_sqft, last_sale_date, last_sale_price, use_code, qualifies, is_preferred, census_median_age, raw")
        .eq("id", parcelId)
        .maybeSingle();
      if (cancelled) return;
      setParcel(data as ParcelRow | null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [parcelId]);

  const visible = useMemo(() => {
    if (!parcel) return [];
    return FIELDS
      .filter((f) => enabled.has(f.key))
      .map((f) => ({ ...f, value: f.format(parcel) }))
      .filter((f) => f.value != null && f.value !== "");
  }, [parcel, enabled]);

  if (!parcelId) return null;
  if (loading) {
    return (
      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-500">
        Loading property intel…
      </div>
    );
  }
  if (!parcel) return null;

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Property Intel</span>
          {parcel.is_preferred ? (
            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[9px] font-semibold text-orange-800">⭐ Preferred</span>
          ) : parcel.qualifies ? (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-800">✓ Qualifies</span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setShowSettings(true)}
          className="rounded p-1 text-xs text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          title="Customize what shows here"
          aria-label="Customize fields"
        >⚙</button>
      </div>

      {visible.length === 0 ? (
        <div className="mt-1 text-xs text-slate-400 italic">All fields hidden — click ⚙ to enable</div>
      ) : (
        <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
          {visible.map((f) => (
            <div key={f.key} className="flex flex-wrap items-baseline gap-x-1">
              <dt className="text-slate-500">{f.label}:</dt>
              <dd className="font-medium text-slate-800">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {showSettings ? (
        <PinIntelSettings
          enabled={enabled}
          onChange={(next) => {
            setEnabled(next);
            saveEnabled(next);
          }}
          onClose={() => setShowSettings(false)}
        />
      ) : null}
    </div>
  );
}

function PinIntelSettings({
  enabled, onChange, onClose,
}: {
  enabled: Set<string>;
  onChange: (next: Set<string>) => void;
  onClose: () => void;
}) {
  function toggle(key: string) {
    const next = new Set(enabled);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
  }
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed inset-x-3 bottom-3 z-50 max-h-[80vh] overflow-y-auto rounded-lg bg-white p-4 shadow-2xl sm:left-1/2 sm:right-auto sm:bottom-auto sm:top-1/2 sm:w-[min(92vw,420px)] sm:-translate-x-1/2 sm:-translate-y-1/2">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">Pin sheet — Property Intel fields</div>
          <button onClick={onClose} className="text-xl leading-none text-slate-400 hover:text-slate-700" aria-label="Close">×</button>
        </div>
        <div className="text-xs text-slate-500">Choose which Regrid fields appear on the pin sheet. Saved per browser.</div>
        <div className="mt-3 space-y-1.5">
          {FIELDS.map((f) => (
            <label key={f.key} className="flex cursor-pointer items-center gap-2 rounded p-1 text-sm hover:bg-slate-50">
              <input
                type="checkbox"
                checked={enabled.has(f.key)}
                onChange={() => toggle(f.key)}
                className="h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-slate-700">{f.label}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-between gap-2 border-t border-slate-100 pt-3">
          <button
            onClick={() => onChange(new Set(DEFAULT_KEYS))}
            className="text-xs font-medium text-orange-600 hover:underline"
          >Reset to default</button>
          <button
            onClick={() => onChange(new Set(ALL_KEYS))}
            className="text-xs font-medium text-slate-500 hover:underline"
          >Show all</button>
          <button
            onClick={() => onChange(new Set())}
            className="text-xs font-medium text-slate-500 hover:underline"
          >Hide all</button>
          <button
            onClick={onClose}
            className="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600"
          >Done</button>
        </div>
      </div>
    </>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────
function fmtInt(n: number | null | undefined): string | null {
  if (n == null) return null;
  return Math.round(n).toLocaleString();
}

// Title-case Regrid's ALL-CAPS owner names. Reorder when it looks like a
// person ("LAST FIRST" or "LAST FIRST M") → "First M Last". Keep entities
// (LLCs, trusts, corporate names) as-is so they don't get garbled.
export function titleCaseOwner(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim();
  // Detect entity / non-person ownership patterns — leave as-is in title case.
  const upper = s.toUpperCase();
  const ENTITY_TAGS = ["LLC", " LP", "LLP", " INC", " CORP", "BANK", "CHURCH", "COUNTY", "CITY OF", "AUTHORITY", "ASSOC", "FOUNDATION", "PARTNERS", "PROPERTIES", "INVESTMENTS", "HOLDINGS", "SCHOOL", "GOVERNMENT", "STATE OF", "USA", "TRUST"];
  const isEntity = ENTITY_TAGS.some((t) => upper.includes(t));
  if (isEntity) return titleCaseSimple(s);

  // Person: try LAST FIRST (M) format common in Regrid.
  // Drop life-estate / etc. suffixes from the end.
  const cleaned = s
    .replace(/\(.+?\)/g, "")           // drop "(LIFE EST)" etc.
    .replace(/\s+&\s+/g, " & ")        // normalize ampersand spacing
    .trim();

  // Multi-owner: just reverse the first segment, leave rest as-is title case
  if (cleaned.includes(" & ")) {
    return titleCaseSimple(cleaned);
  }

  const parts = cleaned.split(/\s+/);
  if (parts.length >= 2) {
    const last = parts[0];
    const rest = parts.slice(1).join(" ");
    return titleCaseSimple(`${rest} ${last}`);
  }
  return titleCaseSimple(cleaned);
}

function titleCaseSimple(s: string): string {
  return s.replace(/\w\S*/g, (w) => {
    // Keep all-caps acronyms ≤2 chars (e.g. "II", "JR", "SR")
    if (w.length <= 2) return w;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  });
}
